import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import StatCards from '../components/StatCards';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DeleteSuccessModal from '../components/DeleteSuccessModal';
import {
  createSubcategory, getAllSubcategories, updateSubcategory,
  deleteSubcategory, getAllCategories
} from '../services/api';
import './Subcategory.css';

var ITEMS_PER_PAGE = 8;
var NAME_LIMIT = 50;
var DESC_LIMIT = 120;
var MAX_IMAGE_BYTES = 3.5 * 1024 * 1024; // 3.5 MB (base64 encoding adds ~33%, staying under the 5MB server JSON limit)

var IMAGE_BASE_URL = (
  process.env.VITE_BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_IMAGE_URL ||
  'https://api.ishmaijewellery.com'
).replace(/\/+$/, '');

var resolveImageUrl = function (url) {
  if (!url || typeof url !== 'string' || !url.trim()) return '';
  var u = url.trim();
  if (u.startsWith('blob:')) return u;
  if (u.startsWith('data:')) return u;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  u = u.replace(/\\/g, '/');
  var uploadsIndex = u.toLowerCase().indexOf('/uploads/');
  if (uploadsIndex >= 0) u = u.slice(uploadsIndex + 1);
  return IMAGE_BASE_URL + '/' + u.replace(/^\/+/, '');
};

var extractImageSrc = function (obj) {
  if (!obj || typeof obj !== 'object') return '';
  var strFields = ['image', 'imageUrl', 'image_url', 'imageURL', 'img',
    'photo', 'thumbnail', 'picture', 'cover', 'avatar', 'banner', 'url'];
  for (var i = 0; i < strFields.length; i++) {
    var v = obj[strFields[i]];
    if (v && typeof v === 'string' && v.trim()) return resolveImageUrl(v.trim());
  }
  var arrFields = ['images', 'photos', 'pictures', 'thumbnails'];
  for (var j = 0; j < arrFields.length; j++) {
    var arr = obj[arrFields[j]];
    if (Array.isArray(arr) && arr.length > 0) {
      var first = arr[0];
      if (typeof first === 'string' && first.trim()) return resolveImageUrl(first.trim());
      if (first && typeof first === 'object') {
        var n = first.url || first.src || first.path || first.image;
        if (n && typeof n === 'string') return resolveImageUrl(n.trim());
      }
    }
  }
  return '';
};

var extractImageRaw = function (obj) {
  if (!obj || typeof obj !== 'object') return '';
  var strFields = ['image', 'imageUrl', 'image_url', 'imageURL', 'img',
    'photo', 'thumbnail', 'picture', 'cover', 'avatar', 'banner', 'url'];
  for (var i = 0; i < strFields.length; i++) {
    var v = obj[strFields[i]];
    if (v && typeof v === 'string' && v.trim()) return v.trim();
  }
  var arrFields = ['images', 'photos', 'pictures', 'thumbnails'];
  for (var j = 0; j < arrFields.length; j++) {
    var arr = obj[arrFields[j]];
    if (Array.isArray(arr) && arr.length > 0) {
      var first = arr[0];
      if (typeof first === 'string' && first.trim()) return first.trim();
      if (first && typeof first === 'object') {
        var n = first.url || first.src || first.path || first.image;
        if (n && typeof n === 'string' && n.trim()) return n.trim();
      }
    }
  }
  return '';
};

// -- Order helper: sorts subcategories by numeric `order` field (ascending).
// Subcategories without an `order` value are treated as 0 and keep their
// original relative position among other unordered items. --
var sortByOrder = function (list) {
  return list.slice().sort(function (a, b) {
    var ao = (a && a.order !== undefined && a.order !== null) ? Number(a.order) : 0;
    var bo = (b && b.order !== undefined && b.order !== null) ? Number(b.order) : 0;
    return ao - bo;
  });
};

var isActiveStatus = function (item) {
  return String((item && item.status) || 'active').toLowerCase() === 'active';
};

var getSubcategoryDate = function (subcategory) {
  return subcategory && (subcategory.createdAt || subcategory.created_at || subcategory.date || subcategory.updatedAt || subcategory.updated_at);
};

var dateInputValue = function (value) {
  if (!value) return '';
  var date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
};

// -- Icons ----------------------------------------------------------------------
var EyeIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
    React.createElement('circle', { cx: 12, cy: 12, r: 3 })
  );
};
var PencilIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
    React.createElement('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })
  );
};
var TrashIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    React.createElement('path', { d: 'M10 11v6M14 11v6' }),
    React.createElement('path', { d: 'M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2' })
  );
};
var UploadIcon = function () {
  return React.createElement('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
    React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
    React.createElement('polyline', { points: '21 15 16 10 5 21' })
  );
};
var XIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};

var NoImgPlaceholder = function () {
  return React.createElement('div', {
    style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(62,124,140,0.08)', borderRadius: 6 }
  },
    React.createElement('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: '#475569', strokeWidth: 1.5 },
      React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
      React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
      React.createElement('polyline', { points: '21 15 16 10 5 21' })
    )
  );
};

// -- Custom Select (matches theme, no native browser highlight) -------------
var CustomSelect = function (props) {
  var value = props.value; var onChange = props.onChange; var options = props.options;
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var ref = useRef();

  useEffect(function () {
    var handler = function (e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var selected = options.find(function (o) { return o.value === value; });

  return React.createElement('div', { ref: ref, style: { position: 'relative' } },
    React.createElement('button', {
      type: 'button',
      onClick: function () { setOpen(!open); },
      style: {
        width: '100%', textAlign: 'left', cursor: 'pointer',
        padding: '11px 14px', borderRadius: 10,
        border: open ? '1px solid #3E7C8C' : '1px solid rgba(62,124,140,0.28)',
        background: '#FFFFFF',
        color: '#102a33',
        fontFamily: "'Outfit',sans-serif", fontSize: 13,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }
    },
      selected ? selected.label : 'Select',
      React.createElement('span', { style: { fontSize: 11, color: '#2d5f6e', marginLeft: 8 } }, '\u25BC')
    ),
    open && React.createElement('div', {
      style: {
        position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
        background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
        borderRadius: 10, overflow: 'hidden', zIndex: 50,
        boxShadow: '0 16px 40px rgba(15,23,42,0.16)',
      }
    },
      options.map(function (o) {
        var isSel = o.value === value;
        return React.createElement('div', {
          key: o.value,
          onClick: function () { onChange(o.value); setOpen(false); },
          style: {
            padding: '10px 14px', fontSize: 13, cursor: 'pointer',
            color: isSel ? '#ffffff' : '#102a33',
            background: isSel ? '#3E7C8C' : '#ffffff',
            fontFamily: "'Outfit',sans-serif",
            transition: 'background 0.15s',
          },
          onMouseEnter: function (e) { if (!isSel) e.currentTarget.style.background = '#eef8fa'; },
          onMouseLeave: function (e) { if (!isSel) e.currentTarget.style.background = '#ffffff'; }
        }, o.label);
      })
    )
  );
};

var SubImage = function (props) {
  var sub = props.sub; var alt = props.alt;
  var es = useState(false); var err = es[0]; var setErr = es[1];
  var src = extractImageSrc(sub);
  useEffect(function () { setErr(false); }, [src]); // eslint-disable-line
  if (!src || err) return React.createElement(NoImgPlaceholder, null);
  return React.createElement('img', {
    src: src, alt: alt || 'subcategory',
    style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 6 },
    onError: function () { setErr(true); }
  });
};

// -- Image Size Warning Popup ---------------------------------------------------
// z-index 99999 ensures it floats above the edit/add modal (which has lower z-index)
var ImageTypeWarningModal = function (props) {
  var onClose = props.onClose;
  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.80)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999,
      padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 440,
        borderRadius: 20,
        background: '#ffffff',
        border: '2px solid #3E7C8C',
        boxShadow: '0 32px 80px rgba(62,124,140,0.16)',
        padding: '44px 36px 36px',
        textAlign: 'center',
      }
    },
      // Warning icon circle
      React.createElement('div', {
        style: {
          width: 76, height: 76, borderRadius: '50%',
          margin: '0 auto 24px',
          background: '#eff6ff',
          border: '3px solid #3E7C8C',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }
      },
        React.createElement('svg', {
          width: 38, height: 38, viewBox: '0 0 24 24',
          fill: 'none', stroke: '#3E7C8C', strokeWidth: 2.2,
          strokeLinecap: 'round', strokeLinejoin: 'round'
        },
          React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
          React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
          React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
        )
      ),
      React.createElement('h3', {
        style: { color: '#3E7C8C', fontSize: 22, fontWeight: 800, margin: '0 0 14px', fontFamily: "'Outfit',sans-serif" }
      }, 'Invalid File Type!'),
      React.createElement('p', {
        style: { color: '#111827', fontSize: 16, margin: '0 0 10px', lineHeight: 1.6, fontFamily: "'Outfit',sans-serif", fontWeight: 600 }
      },
        'Please upload an image in ',
        React.createElement('span', { style: { color: '#F28C28' } }, 'JPG or PNG format'), '.'
      ),
      React.createElement('p', {
        style: { color: '#475569', fontSize: 13, margin: '0 0 32px', fontFamily: "'Outfit',sans-serif" }
      }, 'Only JPG and PNG files are supported. Convert your image and try again.'),
      React.createElement('button', {
        onClick: onClose,
        style: {
          padding: '13px 52px', borderRadius: 12,
          border: 'none', background: 'linear-gradient(90deg, #3E7C8C, #F28C28)',
          color: '#fff', fontWeight: 700, fontSize: 15,
          cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
          boxShadow: '0 8px 24px rgba(62,124,140,0.24)',
        }
      }, 'OK, Got it')
    )
  );
};

var ImageSizeWarningPopup = function (props) {
  var onClose = props.onClose;
  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.80)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999,
      padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 440,
        borderRadius: 20,
        background: '#ffffff',
        border: '2px solid #3E7C8C',
        boxShadow: '0 32px 80px rgba(62,124,140,0.16)',
        padding: '44px 36px 36px',
        textAlign: 'center',
      }
    },
      // Warning icon circle
      React.createElement('div', {
        style: {
          width: 76, height: 76, borderRadius: '50%',
          margin: '0 auto 24px',
          background: '#eff6ff',
          border: '3px solid #3E7C8C',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }
      },
        React.createElement('svg', {
          width: 38, height: 38, viewBox: '0 0 24 24',
          fill: 'none', stroke: '#3E7C8C', strokeWidth: 2.2,
          strokeLinecap: 'round', strokeLinejoin: 'round'
        },
          React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
          React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
          React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
        )
      ),
      React.createElement('h3', {
        style: { color: '#3E7C8C', fontSize: 22, fontWeight: 800, margin: '0 0 14px', fontFamily: "'Outfit',sans-serif" }
      }, 'Image Size Too Large!'),
      React.createElement('p', {
        style: { color: '#111827', fontSize: 16, margin: '0 0 10px', lineHeight: 1.6, fontFamily: "'Outfit',sans-serif", fontWeight: 600 }
      },
        'Please upload an image of size\u00A0',
        React.createElement('span', { style: { color: '#F28C28' } }, '3.5\u00A0MB or below'), '.'
      ),
      React.createElement('p', {
        style: { color: '#475569', fontSize: 13, margin: '0 0 32px', fontFamily: "'Outfit',sans-serif" }
      }, 'Your file exceeds the 3.5\u00A0MB limit. Compress it using TinyPNG or Squoosh and try again.'),
      React.createElement('button', {
        onClick: onClose,
        style: {
          padding: '13px 52px', borderRadius: 12,
          border: 'none', background: 'linear-gradient(90deg, #3E7C8C, #F28C28)',
          color: '#fff', fontWeight: 700, fontSize: 15,
          cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
          boxShadow: '0 8px 24px rgba(62,124,140,0.24)',
        }
      }, 'OK, Got it')
    )
  );
};

// -- Helpers --------------------------------------------------------------------
var resolveId = function (field) {
  if (!field) return '';
  if (typeof field === 'object' && (field._id || field.id)) return field._id || field.id;
  if (typeof field === 'string') return field;
  return '';
};

var resolveName = function (field, list) {
  if (field && typeof field === 'object' && field.name) return field.name;
  if (typeof field === 'string' && field) {
    var f = (list || []).find(function (i) { return i._id === field || i.id === field; });
    if (f && f.name) return f.name;
  }
  return '';
};

var extractArray = function (res) {
  try {
    var d = res && res.data;
    if (!d) return [];
    var arr = [];
    if (Array.isArray(d)) { arr = d; }
    else if (d.data && Array.isArray(d.data)) { arr = d.data; }
    else if (d.result && Array.isArray(d.result)) { arr = d.result; }
    else if (d.subcategories && Array.isArray(d.subcategories)) { arr = d.subcategories; }
    else if (d.categories && Array.isArray(d.categories)) { arr = d.categories; }
    else if (d.data && d.data.subcategories && Array.isArray(d.data.subcategories)) { arr = d.data.subcategories; }
    else if (d.data && d.data.categories && Array.isArray(d.data.categories)) { arr = d.data.categories; }
    else if (d.data && d.data.result && Array.isArray(d.data.result)) { arr = d.data.result; }
    else if (d.data && d.data.data && Array.isArray(d.data.data)) { arr = d.data.data; }
    else if (d._id || (d.data && d.data._id)) { arr = [d._id ? d : d.data]; }
    return arr.filter(function (item) { return item && item.isDeleted !== true && !item.deletedAt; });
  } catch (e) { console.error('extractArray:', e); return []; }
};

var pgBtn = function (disabled, active) {
  return {
    minWidth: 34, height: 34, padding: '0 10px', borderRadius: 8,
    border: active ? '1px solid #14b8a6' : '1px solid rgba(255,255,255,0.2)',
    background: active ? 'rgba(16,185,129,0.35)' : disabled ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
    color: '#ffffff',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13,
    fontWeight: active ? 700 : 500, fontFamily: "'Outfit',sans-serif",
    transition: 'all .15s', opacity: disabled ? 0.35 : 1,
  };
};

var Pagination = function (props) {
  var cp = props.currentPage; var tp = props.totalPages;
  var opc = props.onPageChange; var ti = props.totalItems;
  if (tp <= 1) return null;
  var si = (cp - 1) * ITEMS_PER_PAGE + 1;
  var ei = Math.min(cp * ITEMS_PER_PAGE, ti);
  var nums = (function () {
    var pages = []; var mv = 5;
    var s = Math.max(1, cp - Math.floor(mv / 2));
    var e = Math.min(tp, s + mv - 1);
    if (e - s + 1 < mv) s = Math.max(1, e - mv + 1);
    for (var i = s; i <= e; i++) pages.push(i);
    return pages;
  })();
  var btns = [
    React.createElement('button', { key: 'f', onClick: function () { opc(1); }, disabled: cp === 1, style: pgBtn(cp === 1, false) }, '\u00AB'),
    React.createElement('button', { key: 'p', onClick: function () { opc(cp - 1); }, disabled: cp === 1, style: pgBtn(cp === 1, false) }, '\u2039'),
  ];
  nums.forEach(function (n) { btns.push(React.createElement('button', { key: n, onClick: function () { opc(n); }, style: pgBtn(false, n === cp) }, n)); });
  btns.push(React.createElement('button', { key: 'n', onClick: function () { opc(cp + 1); }, disabled: cp === tp, style: pgBtn(cp === tp, false) }, '\u203A'));
  btns.push(React.createElement('button', { key: 'l', onClick: function () { opc(tp); }, disabled: cp === tp, style: pgBtn(cp === tp, false) }, '\u00BB'));
  return React.createElement('div', { className: 'pagination-row' },
    React.createElement('span', { className: 'pagination-info' }, 'Showing ', si, '\u2013', ei, ' of ', ti, ' items'),
    React.createElement('div', { style: { display: 'flex', gap: 6, alignItems: 'center' } }, btns)
  );
};

// -- Main Component -------------------------------------------------------------
var Subcategory = function () {
  var navigate = useNavigate();
  var b1 = useState([]); var subcategories = b1[0]; var setSubcategories = b1[1];
  var b2 = useState([]); var categories = b2[0]; var setCategories = b2[1];
  var b3 = useState(''); var name = b3[0]; var setName = b3[1];
  var b4 = useState(''); var description = b4[0]; var setDescription = b4[1];
  var b5 = useState(''); var categoryId = b5[0]; var setCategoryId = b5[1];
  var b6 = useState(null); var editId = b6[0]; var setEditId = b6[1];
  var b7 = useState({ text: '', type: '' }); var msg = b7[0]; var setMsg = b7[1];
  var b8 = useState(false); var showModal = b8[0]; var setShowModal = b8[1];
  var b9 = useState(false); var loading = b9[0]; var setLoading = b9[1];
  var b10 = useState(false); var submitLoading = b10[0]; var setSubmitLoading = b10[1];
  var b11 = useState(1); var currentPage = b11[0]; var setCurrentPage = b11[1];
  var b12 = useState(null); var viewSub = b12[0]; var setViewSub = b12[1];
  var b13 = useState(null); var imageFile = b13[0]; var setImageFile = b13[1];
  var b14 = useState(''); var imagePreview = b14[0]; var setImagePreview = b14[1];
  var b15 = useState(''); var imageUrl = b15[0]; var setImageUrl = b15[1];
  var b16 = useState(''); var originalImage = b16[0]; var setOriginalImage = b16[1];
  var b17 = useState(false); var imageRemoved = b17[0]; var setImageRemoved = b17[1];
  var b18 = useState(null); var pendingDelete = b18[0]; var setPendingDelete = b18[1];
  var b19 = useState(false); var showSizeWarn = b19[0]; var setShowSizeWarn = b19[1];
  var b21 = useState(false); var showTypeWarn = b21[0]; var setShowTypeWarn = b21[1];
  var b22 = useState(''); var nameError = b22[0]; var setNameError = b22[1];
  var b24 = useState(''); var order = b24[0]; var setOrder = b24[1]; // -- Display Order --
  var b25 = useState('active'); var status = b25[0]; var setStatus = b25[1];
  var bSF = useState('all'); var statusFilter = bSF[0]; var setStatusFilter = bSF[1];
  var bUS = useState(false); var showUpdateSuccess = bUS[0]; var setShowUpdateSuccess = bUS[1];
  var bDS = useState(null); var deleteSuccess = bDS[0]; var setDeleteSuccess = bDS[1];

  var searchState = useState(''); var search = searchState[0]; var setSearch = searchState[1];
  var fromDateState = useState(''); var fromDate = fromDateState[0]; var setFromDate = fromDateState[1];
  var toDateState = useState(''); var toDate = toDateState[0]; var setToDate = toDateState[1];

  var fileInputRef = useRef();

  var showMsg = function (text, type) {
    setMsg({ text: text, type: type });
    setTimeout(function () { setMsg({ text: '', type: '' }); }, 5000);
  };

  var resetFileInput = function () {
    try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (e) { /* noop */ }
  };

  var clearImage = function () {
    setImagePreview(''); setImageFile(null); setImageUrl(''); setImageRemoved(true);
    resetFileInput();
  };

  var resetForm = function () {
    setName(''); setDescription(''); setCategoryId(''); setEditId(null); setOrder(''); setStatus('active'); clearImage(); setNameError('');
  };

  var closeModal = function () {
    setShowModal(false); resetForm(); setMsg({ text: '', type: '' });
  };

  // -- Name validation helper (A-Z, a-z, 0-9, spaces, and: + & - ) --
  var validateName = function (nameValue) {
    var nameToValidate = nameValue || name;
    var isValid = /^[a-zA-Z0-9\s+&\-]*$/.test(nameToValidate);
    if (!isValid) {
      setNameError('Special characters are not allowed');
      return false;
    }
    setNameError('');
    return true;
  };

  // -- SINGLE validation gate used by BOTH click-pick and drag-drop ---------
  var validateAndSetImage = function (file) {
    if (!file) return;

    // Step 1: must be JPG or PNG
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      resetFileInput();
      setShowTypeWarn(true);  // show type warning modal
      return;
    }

    // Step 2: must be <= 3.5 MB
    if (file.size > MAX_IMAGE_BYTES) {
      resetFileInput();        // wipe <input> so Chrome doesn't cache the bad file
      setShowSizeWarn(true);   // show popup
      return;
    }

    // Valid
    setImageFile(file);
    setImageUrl('');
    setImagePreview(URL.createObjectURL(file));
    setImageRemoved(false);
  };

  var handleImageChange = function (e) {
    validateAndSetImage(e.target.files && e.target.files[0]);
  };

  var handleImageUrlChange = function (e) {
    var val = e.target.value || '';
    setImageUrl(val);
    setImageRemoved(false);
    if (val.trim()) {
      if (imageFile) { setImageFile(null); resetFileInput(); }
      setImagePreview(resolveImageUrl(val));
    } else if (!imageFile && !imageRemoved) {
      setImagePreview('');
    }
  };

  var handleImageDrop = function (e) {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    validateAndSetImage(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  var fetchData = function () {
    setLoading(true);
    Promise.all([getAllSubcategories(), getAllCategories()])
      .then(function (results) {
        var subs = extractArray(results[0]);
        var cats = extractArray(results[1]);
        setSubcategories(subs); setCategories(cats); setCurrentPage(1);
      })
      .catch(function (err) {
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Failed to load data', 'e');
      })
      .finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchData(); }, []); // eslint-disable-line

  var handleSubmit = async function () {
    var trimmedName = name.trim();
    if (!trimmedName) { showMsg('Subcategory name is required', 'e'); return; }
    if (!validateName(trimmedName)) { showMsg('Special characters are not allowed', 'e'); return; }
    if (!categoryId) { showMsg('Please select a category', 'e'); return; }

    var trimmedOrder = order.toString().trim();
    if (trimmedOrder !== '' && isNaN(Number(trimmedOrder))) {
      showMsg('Display order must be a number', 'e');
      return;
    }

    if (imageFile && imageFile.size > MAX_IMAGE_BYTES) {
      setImageFile(null); setImagePreview(''); resetFileInput();
      setShowSizeWarn(true);
      return;
    }

    setSubmitLoading(true);

    var onSuccess = function () {
      closeModal();
      if (editId) {
        setShowUpdateSuccess(true);
      } else {
        showMsg('Subcategory Created! \u2705', 's');
      }
      fetchData();
      setSubmitLoading(false);
    };

    var onFail = function (err) {
      console.error('Submit failed:', err);
      showMsg((err.response && err.response.data && (err.response.data.message || err.response.data.msg)) || 'An error occurred', 'e');
      setSubmitLoading(false);
    };

    var trimmedImageUrl = imageUrl.trim();
    var hasNewUrl = trimmedImageUrl && trimmedImageUrl !== originalImage;
    var orderValue = trimmedOrder !== '' ? Number(trimmedOrder) : 0;

    try {
      var fd = new FormData();
      fd.append('name', trimmedName);
      fd.append('description', description.trim());
      fd.append('category', categoryId);
      fd.append('categoryId', categoryId);
      fd.append('status', status);
      fd.append('order', String(orderValue));
      if (imageRemoved) {
        fd.append('removeImage', 'true');
      } else if (imageFile) {
        fd.append('image', imageFile);
      } else if (hasNewUrl) {
        fd.append('imageUrl', trimmedImageUrl);
      }

      await (editId ? updateSubcategory(editId, fd) : createSubcategory(fd));
      onSuccess();
    } catch (err) {
      onFail(err);
    }
  };

  var handleEdit = function (sub) {
    setEditId(sub._id || sub.id);
    setName(sub.name || '');
    setNameError('');
    setDescription(sub.description || '');
    setCategoryId(resolveId(sub.category) || resolveId(sub.categoryId) || '');
    setOrder((sub.order !== undefined && sub.order !== null) ? String(sub.order) : '');
    setStatus(sub.status || 'active');
    var existingImg = extractImageRaw(sub) || '';
    setOriginalImage(existingImg);
    setImageUrl('');
    setImagePreview(extractImageSrc(sub));
    setImageFile(null);
    setImageRemoved(false);
    setShowModal(true);
  };

  var handleDelete = function (sub) {
    var subId = sub._id || sub.id;
    if (!subId) { showMsg('Cannot delete: missing ID', 'e'); return; }
    deleteSubcategory(subId)
      .then(function () {
        setDeleteSuccess({ id: subId, name: sub.name || sub.title });
      })
      .catch(function (err) {
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Delete failed', 'e');
      });
  };

  var handleDeleteSuccessClose = function () {
    var id = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (id) {
      setSubcategories(function (prev) { return prev.filter(function (s) { return (s._id || s.id) !== id; }); });
    }
    fetchData();
  };

  var requestDelete = function (sub) { if (!sub) { showMsg('Cannot delete: invalid item', 'e'); return; } setPendingDelete(sub); };
  var cancelDelete = function () { setPendingDelete(null); };
  var confirmDelete = function () { if (!pendingDelete) return; var s = pendingDelete; setPendingDelete(null); handleDelete(s); };

  var handleStatCardClick = function (filterVal) {
    setStatusFilter(filterVal);
    setCurrentPage(1);
  };

  // -- Subcategories, sorted by Display Order (ascending), filtered by status, search, and date, THEN paginated --
  var filteredSubcategories = sortByOrder(subcategories).filter(function (s) {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return isActiveStatus(s);
    if (statusFilter === 'inactive') return !isActiveStatus(s);
    return true;
  }).filter(function (s) {
    var query = search.trim().toLowerCase();
    var catName = resolveName(s.category, categories) || resolveName(s.categoryId, categories) || '';
    if (query && [s.name, s.description, catName].join(' ').toLowerCase().indexOf(query) === -1) return false;
    var subDate = dateInputValue(getSubcategoryDate(s));
    if (fromDate && (!subDate || subDate < fromDate)) return false;
    if (toDate && (!subDate || subDate > toDate)) return false;
    return true;
  });

  var activeSubcategories = subcategories.filter(isActiveStatus).length;
  var inactiveSubcategories = subcategories.filter(function (s) { return !isActiveStatus(s); }).length;
  var subcategoryStatCards = [
    { title: 'Total Subcategories', value: subcategories.length, description: 'All subcategory records', icon: 'layers', accent: '#d99a2b', active: statusFilter === 'all', onClick: function () { setStatusFilter('all'); setCurrentPage(1); } },
    { title: 'Active Subcategories', value: activeSubcategories, description: 'Visible in storefront', icon: 'check', accent: '#16a34a', active: statusFilter === 'active', onClick: function () { handleStatCardClick('active'); } },
    { title: 'Inactive Subcategories', value: inactiveSubcategories, description: 'Hidden from users', icon: 'pause', accent: '#f59e0b', active: statusFilter === 'inactive', onClick: function () { handleStatCardClick('inactive'); } },
    { title: 'Total Categories', value: categories.length, description: 'Available parent categories', icon: 'grid', accent: '#0ea5e9', onClick: function () { navigate('/admin/category'); } },
  ];
  var totalPages = Math.max(1, Math.ceil(filteredSubcategories.length / ITEMS_PER_PAGE));
  var pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  var pageItems = filteredSubcategories.slice(pageStart, pageStart + ITEMS_PER_PAGE);
  var thumbStyle = { width: 44, height: 44, minWidth: 44, flexShrink: 0, borderRadius: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(62,124,140,0.08)', border: '1px solid rgba(62,124,140,0.15)' };

  useEffect(function () {
    setCurrentPage(1);
  }, [search, fromDate, toDate, statusFilter]);

  useEffect(function () {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return React.createElement(AdminLayout, { hideLayoutOnModal: !!(viewSub || showModal || pendingDelete || showUpdateSuccess || deleteSuccess) },

    // -- Update Success Modal --
    showUpdateSuccess && React.createElement(UpdateSuccessModal, {
      isOpen: true,
      message: 'Updated successfully!',
      onClose: function () { setShowUpdateSuccess(false); }
    }),

    // -- Size warning popup -- rendered FIRST at top level, z-index 99999 -----
    showSizeWarn && React.createElement(ImageSizeWarningPopup, { onClose: function () { setShowSizeWarn(false); } }),

    // -- Type warning popup -- rendered SECOND at top level, z-index 99999 -----
    showTypeWarn && React.createElement(ImageTypeWarningModal, { onClose: function () { setShowTypeWarn(false); } }),

    // -- View Modal -------------------------------------------------------------
    viewSub && React.createElement('div', { className: 'modal-overlay', onClick: function (e) { if (e.target.classList.contains('modal-overlay')) setViewSub(null); } },
      React.createElement('div', { className: 'view-modal-box' },
        React.createElement('div', { className: 'modal-hdr' },
          React.createElement('span', { className: 'modal-title' }, 'Subcategory Details'),
          React.createElement('button', { className: 'modal-close', onClick: function () { setViewSub(null); } }, React.createElement(XIcon, null))
        ),
        React.createElement('div', { className: 'view-img-wrap' },
          (function () {
            var src = extractImageSrc(viewSub);
            if (!src) return React.createElement('div', { style: { width: '100%', textAlign: 'center', padding: '28px 0', color: '#475569' } },
              React.createElement('svg', { width: 48, height: 48, viewBox: '0 0 24 24', fill: 'none', stroke: '#475569', strokeWidth: 1 },
                React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
                React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
                React.createElement('polyline', { points: '21 15 16 10 5 21' })
              ),
              React.createElement('div', { style: { fontSize: 11, marginTop: 6 } }, 'No image')
            );
            return React.createElement('img', { src: src, alt: viewSub.name, className: 'view-img' });
          })()
        ),
        React.createElement('div', { className: 'view-details' },
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-lbl' }, 'Name'),
            React.createElement('span', { className: 'view-val' }, viewSub.name)
          ),
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-lbl' }, 'Category'),
            React.createElement('span', { className: 'view-val' }, resolveName(viewSub.category, categories) || resolveName(viewSub.categoryId, categories) || '\u2014')
          ),
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-lbl' }, 'Display Order'),
            React.createElement('span', { className: 'view-val' }, (viewSub.order !== undefined && viewSub.order !== null) ? viewSub.order : 0)
          ),
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-lbl' }, 'Status'),
            React.createElement('span', { className: 'bdg ' + ((viewSub.status || 'active').toLowerCase() === 'inactive' ? 'bdg-b' : 'bdg-g') }, (viewSub.status || 'active').toUpperCase())
          ),
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-lbl' }, 'Description'),
            React.createElement('span', { className: 'view-val' }, viewSub.description || '\u2014')
          )
        )
      )
    ),

    // -- Add / Edit Modal --------------------------------------------------------
    showModal && React.createElement('div', { className: 'modal-overlay', onClick: function (e) { if (e.target.classList.contains('modal-overlay')) closeModal(); } },
      React.createElement('div', { className: 'modal-box' },
        React.createElement('div', { className: 'modal-hdr' },
          React.createElement('span', { className: 'modal-title' }, editId ? 'Edit Subcategory' : 'Add Subcategory'),
          React.createElement('button', { className: 'modal-close', onClick: closeModal }, React.createElement(XIcon, null))
        ),
        msg.text && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),
        React.createElement('div', { className: 'fg' },
          React.createElement('div', { className: 'fg-header' },
            React.createElement('label', null, 'Subcategory Name ', React.createElement('span', { className: 'req' }, '*')),
            React.createElement('span', { className: 'char-counter' }, name.length, '/', NAME_LIMIT)
          ),
          React.createElement('input', { type: 'text', placeholder: 'e.g. White Board', value: name, maxLength: NAME_LIMIT, onChange: function (e) { setName(e.target.value); validateName(e.target.value); }, style: nameError ? { borderColor: '#ef4444' } : {} }),
          nameError && React.createElement('span', { className: 'err-msg' }, nameError)
        ),
        React.createElement('div', { className: 'fg' },
          React.createElement('label', null, 'Parent Category ', React.createElement('span', { className: 'req' }, '*')),
          React.createElement(CustomSelect, {
            value: categoryId,
            onChange: function (val) { setCategoryId(val); },
            options: categories.map(function (c) { return { value: c._id || c.id, label: c.name }; })
          })
        ),
        React.createElement('div', { className: 'fg' },
          React.createElement('label', null, 'Display Order'),
          React.createElement('input', {
            type: 'number',
            min: 0,
            step: 1,
            placeholder: '0 (numeric order)',
            value: order,
            onChange: function (e) { setOrder(e.target.value); }
          })
        ),
        React.createElement('div', { className: 'fg' },
          React.createElement('label', null, 'Status'),
          React.createElement(CustomSelect, {
            value: status,
            onChange: function (val) { setStatus(val); },
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]
          })
        ),
        React.createElement('div', { className: 'fg' },
          React.createElement('div', { className: 'fg-header' },
            React.createElement('label', null, 'Description'),
            React.createElement('span', { className: 'char-counter' }, description.length, '/', DESC_LIMIT)
          ),
          React.createElement('textarea', { placeholder: 'Brief description...', value: description, maxLength: DESC_LIMIT, onChange: function (e) { setDescription(e.target.value); } })
        ),
        React.createElement('div', { className: 'fg' },
          React.createElement('label', null, 'Subcategory Image'),
          imagePreview
            ? React.createElement('div', { className: 'img-preview-box' },
              React.createElement('img', { src: imagePreview, alt: 'Preview', className: 'img-preview' }),
              React.createElement('button', { type: 'button', className: 'rmv-img-btn', onClick: clearImage, title: 'Remove Image' }, React.createElement(XIcon, null))
            )
            : React.createElement('div', {
              className: 'drop-area',
              onClick: function () { if (fileInputRef.current) fileInputRef.current.click(); },
              onDragOver: function (e) { e.preventDefault(); e.currentTarget.classList.add('drag-over'); },
              onDragLeave: function (e) { e.currentTarget.classList.remove('drag-over'); },
              onDrop: handleImageDrop,
            },
              React.createElement(UploadIcon, null),
              React.createElement('p', null, 'Drag & drop image here, or ', React.createElement('span', null, 'browse')),
              React.createElement('span', { className: 'drop-hint' }, 'Supports PNG, JPG (max 3.5 MB)')
            ),
          React.createElement('input', { ref: fileInputRef, type: 'file', accept: 'image/png,image/jpeg', style: { display: 'none' }, onChange: handleImageChange }),
          React.createElement('div', { className: 'url-input-wrap' },
            React.createElement('span', { className: 'url-lbl' }, 'or Image URL'),
            React.createElement('input', { type: 'text', placeholder: 'https://...', value: imageUrl, onChange: handleImageUrlChange })
          )
        ),
        React.createElement('div', { className: 'm-actions' },
          React.createElement('button', { className: 'btn btn-s', type: 'button', onClick: closeModal }, 'Cancel'),
          React.createElement('button', { className: 'btn btn-p', type: 'button', disabled: submitLoading, style: { opacity: submitLoading ? 0.7 : 1 }, onClick: handleSubmit }, submitLoading ? 'Saving...' : (editId ? 'Update Subcategory' : 'Add Subcategory'))
        )
      )
    ),
    // -- Delete Confirm Modal ----------------------------------------------------
    pendingDelete && React.createElement(DeleteConfirmModal, {
      itemName: pendingDelete.name || pendingDelete.title,
      message: 'Are you sure you want to delete this subcategory?',
      onConfirm: confirmDelete,
      onCancel: cancelDelete
    }),
    // -- Delete Success Modal ----------------------------------------------------
    deleteSuccess && React.createElement(DeleteSuccessModal, {
      isOpen: true,
      message: 'Deleted Successfully',
      subtitle: 'The subcategory has been deleted successfully.',
      onClose: handleDeleteSuccessClose
    }),

    msg.text && !showModal && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),

    React.createElement('div', { className: 'page-header-row' },
      React.createElement('div', { className: 'page-header-main' },
        React.createElement('div', { className: 'page-header-title-row' },
          React.createElement('div', { className: 'page-header-icon' },
            React.createElement('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
              React.createElement('path', { d: 'M12 2C8 7 6.5 10.8 6.5 14.5A5.5 5.5 0 0 0 12 20a5.5 5.5 0 0 0 5.5-5.5C17.5 10.8 16 7 12 2z' }),
              React.createElement('circle', { cx: 12, cy: 11, r: 1.5 })
            )
          ),
          React.createElement('div', { className: 'ptitle' }, 'Subcategories')
        ),
        React.createElement('div', { className: 'header-subtitle' }, 'Organize and manage your product subcategories efficiently')
      ),
      React.createElement('button', { className: 'add-btn', type: 'button', onClick: function () { resetForm(); setShowModal(true); } },
        React.createElement('svg', { viewBox: '0 0 24 24', style: { width: 15, height: 15, fill: '#fff' } }, React.createElement('path', { d: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' })), ' Add Subcategory'
      )
    ),

    React.createElement(StatCards, { cards: subcategoryStatCards }),

    React.createElement('div', { className: 'wid' },
      React.createElement('div', { className: 'wh' },
        React.createElement('span', { className: 'wt' },
          'All Subcategories (', filteredSubcategories.length, (filteredSubcategories.length !== subcategories.length ? ' of ' + subcategories.length : ''), ')'
        )
      ),

      React.createElement('div', { className: 'category-controls' },
        React.createElement('div', { className: 'category-search-field' },
          React.createElement('span', { className: 'category-search-icon', 'aria-hidden': 'true' }, '\u2315'),
          React.createElement('input', {
            type: 'text',
            value: search,
            placeholder: 'Search subcategories...',
            'aria-label': 'Search subcategories',
            onChange: function (e) { setSearch(e.target.value); }
          })
        ),
        React.createElement('div', { className: 'category-date-item' },
          React.createElement('span', { className: 'category-date-label' }, 'From'),
          React.createElement('input', {
            type: 'date',
            value: fromDate,
            'aria-label': 'Filter subcategories from date',
            onChange: function (e) { setFromDate(e.target.value); }
          })
        ),
        React.createElement('div', { className: 'category-date-item' },
          React.createElement('span', { className: 'category-date-label' }, 'To'),
          React.createElement('input', {
            type: 'date',
            value: toDate,
            min: fromDate || undefined,
            'aria-label': 'Filter subcategories to date',
            onChange: function (e) { setToDate(e.target.value); }
          })
        ),
        (search || fromDate || toDate || statusFilter !== 'all') && React.createElement('button', {
          type: 'button',
          className: 'category-clear-btn',
          onClick: function () { setSearch(''); setFromDate(''); setToDate(''); setStatusFilter('all'); }
        }, 'Clear')
      ),

      loading
        ? React.createElement('div', { className: 'loading-spin' })
        : React.createElement(React.Fragment, null,
          React.createElement('div', { style: { overflowX: 'auto' } },
            React.createElement('table', { className: 'tbl' },
              React.createElement('thead', null,
                React.createElement('tr', null,
                  React.createElement('th', null, 'S.No'), React.createElement('th', null, 'Image'), React.createElement('th', null, 'Name'),
                  React.createElement('th', null, 'Category'), React.createElement('th', null, 'Order'), React.createElement('th', null, 'Description'),
                  React.createElement('th', null, 'Status'), React.createElement('th', { className: 'actions-th' }, 'Actions')
                )
              ),
              React.createElement('tbody', null,
                pageItems.length === 0
                  ? React.createElement('tr', null, React.createElement('td', { colSpan: 8, style: { textAlign: 'center', color: '#94a3b8', padding: 32 } }, 'No subcategories found.'))
                  : pageItems.map(function (sub, idx) {
                    var catName = resolveName(sub.category, categories) || resolveName(sub.categoryId, categories) || '\u2014';
                    var desc = (sub.description && sub.description.trim()) || '\u2014';
                    var status = (sub.status || 'active').toLowerCase();
                    var sid = sub._id || sub.id || String(idx);
                    return React.createElement('tr', { key: sid || idx },
                      React.createElement('td', { style: { color: '#111827', fontSize: 13, fontWeight: 500 } }, pageStart + idx + 1),
                      React.createElement('td', null, React.createElement('div', { style: thumbStyle }, React.createElement(SubImage, { sub: sub, alt: sub.name }))),
                      React.createElement('td', { style: { fontWeight: 600 } }, sub.name),
                      React.createElement('td', { className: 'cat-cell' }, catName),
                      React.createElement('td', { style: { color: '#111827', fontSize: 13, fontWeight: 500 } }, (sub.order !== undefined && sub.order !== null) ? sub.order : 0),
                      React.createElement('td', { className: 'desc-cell', title: desc }, desc),
                      React.createElement('td', null, React.createElement('span', { className: 'bdg ' + (status === 'inactive' ? 'bdg-b' : 'bdg-g') }, status.toUpperCase())),
                      React.createElement('td', { className: 'actions-cell' },
                        React.createElement('div', { className: 'row-actions' },
                          React.createElement('button', {
                            className: 'row-action-btn row-action-view',
                            title: 'View',
                            'aria-label': 'View',
                            type: 'button',
                            onClick: function (e) { e.stopPropagation(); setViewSub(sub); }
                          }, React.createElement(EyeIcon, null)),
                          React.createElement('button', {
                            className: 'row-action-btn row-action-edit',
                            title: 'Edit',
                            'aria-label': 'Edit',
                            type: 'button',
                            onClick: function (e) { e.stopPropagation(); handleEdit(sub); }
                          }, React.createElement(PencilIcon, null)),
                          React.createElement('button', {
                            className: 'row-action-btn row-action-delete',
                            title: 'Delete',
                            'aria-label': 'Delete',
                            type: 'button',
                            onClick: function (e) { e.stopPropagation(); requestDelete(sub); }
                          }, React.createElement(TrashIcon, null))
                        )
                      )
                    );
                  })
              )
            )
          ),
          React.createElement(Pagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: setCurrentPage, totalItems: filteredSubcategories.length })
        )
    )
  );
};

export default Subcategory;
